// @owners { team: patients-team }
import { Toast, ToastContext } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { SentrySpanHub } from '@alto/performance';
import { CartEndpoint, type CartEndpointAddItemParams } from '@alto/scriptdash/alto/patient_app/carts/v1/cart_endpoint';
import { type PriceType } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useState } from 'react';
import {
  addToCart__DEPRECATED,
  fetchWindows,
  // eslint-disable-next-line import/no-deprecated
  selectPaymentType__DEPRECATED,
  updateCartAutoRefill,
  updateOrder,
} from '~shared/actions/checkout';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { fetchNextAvailableDates } from '~shared/actions/nextAvailableDates';
// eslint-disable-next-line import/no-deprecated
import { type BillingOverridePayload, autobillPrescription } from '~shared/actions/prescriptions';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { transformPriceType } from '~shared/features/cart/selectors/getReduxCart';
import { getItemsInOrder } from '~shared/features/checkout/selectors/getItems';
import { type ItemKey } from '~shared/features/checkout/types';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import { getPrescriptionIDsWithCashLessThanInsurancePricingScenarios } from '~shared/features/pricing/selectors/getPrescriptionIDsWithCashLessThanInsurancePricingScenarios';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { apiEndpointHandler } from '~shared/helpers/api';
import { useAnalytics, useIsInCheckout } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { useInvalidatePrescriptions, useOrderPricing, useQueryShipmentFeesAvailable } from '~shared/queries';
import { queries } from '~shared/queries/query-keys';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { homescreenMethods } from '../../homescreen';

const genericErrorMessage = 'There was an error adding to your cart. Please try again later.';
const cartEndpoint = CartEndpoint(apiEndpointHandler);

export type AddToCartParams = {
  resource_id: number;
  resource_type: string;
  quantity?: number | null | undefined;
  auto_refill_enabled?: boolean | null | undefined;
  resetOrderDate?: boolean;
  billing_overrides?: BillingOverridePayload;
};

type AddToCartResult = {
  success: boolean;
};

type UseAddToCart = {
  isDisabled: boolean;
  isPending: boolean;
  isSuccess: boolean;
  handleAddToCart: (params: AddToCartParams) => Promise<AddToCartResult>;
};

type RxProperties = {
  availableForNextDeliveryWindow: boolean | null | undefined;
  delayedAvailabilityReason: string | null | undefined;
  id: number | null | undefined;
  nextAvailableDateFromNow: string | null | undefined;
};

/**
 * Hook used to add an item to the patient's cart. The cart will be created if
 * it doesn't yet exist.
 *
 * // export the errorMessage to render in your component
 * const { handleAddToCart, errorMessage } = useAddToCart();
 *
 * const result = await handleAddToCart({ item, onSuccess: onAddToCartSuccess });
 *
 * // perform logic after the item is successfully added to cart
 * if (result.success) {
 *   ...
 * }
 */

export const useAddToCart = (): UseAddToCart => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const userID = useSelectorShared(getCurrentUserID);
  const cashIsLessThanInsurancePrescriptionIDs = useSelectorShared(
    getPrescriptionIDsWithCashLessThanInsurancePricingScenarios,
  );
  const { addToast } = useContext(ToastContext);
  const { value: serverSideCartEnabled } = Experimentation.useFeatureFlag('server_side_cart');
  const { invalidatePrescriptionsQuery } = useInvalidatePrescriptions();
  const { nextAvailableDatesByPrescriptionID, isLoading: isLoadingNextAvailableDates } =
    useNextAvailableDatesForPrescriptions();
  const { prescriptionIDs } = usePrescriptionsInCart();
  const origin = getOrigin();
  const [isSuccess, setIsSuccess] = useState(false);
  useQueryShipmentFeesAvailable();
  const { isOrderPricingLoading, refetchOrderPricing } = useOrderPricing();
  const itemsInReduxCart = useSelectorShared(getItemsInOrder);
  const isInCheckout = useIsInCheckout();
  const addressID = useNextAvailableDateAddressId();

  // disable add to cart buttons while other requests are in flight so delivery status settles
  // a scheduleable prescription can be removed from the cart if the delivery status is processing
  const isFetching = useSelectorShared((state) => {
    const {
      autobillPrescriptionLoadingByID,
      fetchActiveDeliveriesLoading,
      fetchDeliveriesLoading,
      fetchActivePrescriptionsLoading,
      fetchPrescriptionsLoading,
      fetchNextAvailableDatesLoading,
    } = state.ui.loading;
    return (
      isOrderPricingLoading ||
      fetchNextAvailableDatesLoading ||
      fetchActiveDeliveriesLoading ||
      fetchDeliveriesLoading ||
      fetchActivePrescriptionsLoading ||
      fetchPrescriptionsLoading ||
      Object.values(autobillPrescriptionLoadingByID).some(Boolean)
    );
  });

  const fireCartItemAddedEvent = useCallback(
    ({
      resource_id,
      auto_refill_enabled,
      selected_price_type,
      server_side_cart,
      cart_items,
      cart_id,
    }: {
      resource_id: AddToCartParams['resource_id'];
      auto_refill_enabled: AddToCartParams['auto_refill_enabled'];
      selected_price_type: PriceType | null | undefined;
      server_side_cart: boolean;
      cart_items: { resource_id: number; resource_type: string }[];
      cart_id?: number;
    }) => {
      const rxProperties: RxProperties = {
        availableForNextDeliveryWindow: null,
        delayedAvailabilityReason: null,
        id: null,
        nextAvailableDateFromNow: null,
      };
      const nextAvailableDate = isLoadingNextAvailableDates ? null : nextAvailableDatesByPrescriptionID[resource_id];
      if (nextAvailableDate) {
        rxProperties.nextAvailableDateFromNow = nextAvailableDate.earliest.date;
        rxProperties.availableForNextDeliveryWindow = nextAvailableDate.earliest.reason === 'available';
        rxProperties.delayedAvailabilityReason = nextAvailableDate.earliest.reason;
      }

      const orderResult = auto_refill_enabled ? 'auto refill' : 'one-time fill';
      trackEvent({
        event: EVENTS.CART__ITEM_ADDED,
        params: {
          origin,
          'next available date from now': rxProperties.nextAvailableDateFromNow,
          'available for next delivery window': rxProperties.availableForNextDeliveryWindow,
          'delayed availability reason': rxProperties.delayedAvailabilityReason,
          'order type': orderResult,
          'price type': selected_price_type ? selected_price_type : 'not selected',
          server_side_cart,
          cart_items,
          cart_id,
        },
        additionalFields: {
          prescriptionId: resource_id,
        },
      });
    },
    [isLoadingNextAvailableDates, nextAvailableDatesByPrescriptionID, trackEvent, origin],
  );

  const fireCartItemAddedFailedEvent = useCallback(
    ({
      resource_id,
      auto_refill_enabled,
      selected_price_type,
      server_side_cart,
    }: {
      resource_id: AddToCartParams['resource_id'];
      auto_refill_enabled: AddToCartParams['auto_refill_enabled'];
      selected_price_type: PriceType | null | undefined;
      server_side_cart: boolean;
    }) => {
      trackEvent({
        event: EVENTS.CART__ITEM_ADDED_FAILED,
        params: {
          origin,
          'order type': auto_refill_enabled ? 'auto refill' : 'one-time fill',
          'price type': selected_price_type ? selected_price_type : 'not selected',
          server_side_cart,
        },
        additionalFields: {
          prescriptionId: resource_id,
        },
      });
    },
    [origin, trackEvent],
  );

  const autobillAndRefreshData = useCallback(
    async (
      { resource_id, resource_type, resetOrderDate, billing_overrides }: AddToCartParams,
      cartPrescriptionIDs: number[],
    ) => {
      if (resource_type === 'Prescription') {
        // eslint-disable-next-line import/no-deprecated
        await dispatch(autobillPrescription(resource_id, billing_overrides));
        await refetchOrderPricing();

        // The correct order is to refresh deliveries after autobill, before refreshing prescriptions.
        // Why? Because refreshing prescriptions includes logic to remove any prescriptions from cart that aren't
        // ready to schedule. (see useRemoveUnschedulablePrescriptionsFromCart).
        // A delivery will appear not ready to schedule if it's refreshed during autobill and not after.
        // eslint-disable-next-line import/no-deprecated
        await dispatch(fetchDeliveries());
        invalidatePrescriptionsQuery();

        await dispatch(
          // eslint-disable-next-line import/no-deprecated
          fetchNextAvailableDates({
            bundledPrescriptionIDs: cartPrescriptionIDs,
            unbundledPrescriptionIDs: [resource_id],
            addressID,
          }),
        );
        if (resetOrderDate) {
          // resetOrderDate is passed in for flows that are likely to update the next available date to an
          // earlier date. This is currently used for 'Get it sooner' flows, where patients can choose
          // different payment options as a way to get their medication sooner.
          // In these scenarios, we should reset the order date and have select date component
          // calculate the order date based on the new next available date
          dispatch(updateOrder({ date: undefined }));
        }
      }
      const allPrescriptionIDs = [...prescriptionIDs];
      if (resource_type === 'Prescription' && !allPrescriptionIDs.includes(resource_id)) {
        allPrescriptionIDs.push(resource_id);
      }

      dispatch(fetchWindows(allPrescriptionIDs));
    },
    [addressID, dispatch, invalidatePrescriptionsQuery, prescriptionIDs, refetchOrderPricing],
  );

  const addToCartRedux = useCallback(
    ({
      resource_id,
      auto_refill_enabled,
      selected_price_type,
      reset_order_date,
      billing_overrides,
    }: {
      resource_id: number;
      auto_refill_enabled: boolean;
      selected_price_type?: PriceType;
      reset_order_date?: boolean;
      billing_overrides?: BillingOverridePayload;
    }) => {
      const item: ItemKey = { resource_id, resource_type: 'Prescription' };
      dispatch(addToCart__DEPRECATED(item, reset_order_date, billing_overrides, true));

      if (auto_refill_enabled !== null && auto_refill_enabled !== undefined) {
        dispatch(updateCartAutoRefill(resource_id, auto_refill_enabled));
        trackEvent({
          event: EVENTS.AUTO_REFILL_UI_STATUS_CHANGED,
          params: {
            origin,
            auto_refill_status: auto_refill_enabled ? 'auto refill' : 'one-time fill',
          },
          additionalFields: {
            prescriptionId: resource_id,
          },
        });
      }

      if (selected_price_type) {
        // eslint-disable-next-line import/no-deprecated
        dispatch(selectPaymentType__DEPRECATED(resource_id, selected_price_type));
      }

      return {
        items: itemsInReduxCart.map((item) => ({
          resource_id: item.resource_id,
          resource_type: item.resource_type,
        })),
      };
    },
    [dispatch, itemsInReduxCart, origin, trackEvent],
  );

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (params: CartEndpointAddItemParams) => {
      const response = await cartEndpoint.addItem({ params });
      return { response };
    },
  });

  const addToCartApi = useCallback(
    async ({
      resource_id,
      resource_type,
      quantity,
      auto_refill_enabled,
      selected_price_type,
    }: {
      resource_id: number;
      resource_type: string;
      quantity: number;
      auto_refill_enabled: boolean;
      selected_price_type?: PriceType;
    }) => {
      const requestParams: CartEndpointAddItemParams = {
        resource_id,
        resource_type,
        quantity,
        auto_refill_enabled,
        selected_price_type: transformPriceType(selected_price_type),
      };
      const { response } = await mutateAsync(requestParams);
      if (response.errors?.length) {
        addToast(<Toast variant="error">{genericErrorMessage}</Toast>);
        fireCartItemAddedFailedEvent({
          resource_id,
          auto_refill_enabled,
          selected_price_type,
          server_side_cart: true,
        });
      }

      return {
        success: !response.errors?.length,
        items:
          response.data?.items.map((item) => ({
            resource_id: item.resource_id,
            resource_type: item.resource_type,
          })) || [],
        cart_id: response.data?.id,
      };
    },
    [addToast, fireCartItemAddedFailedEvent, mutateAsync],
  );

  const addToCart = useCallback(
    async (params: AddToCartParams) => {
      setIsSuccess(false);

      try {
        const quantity = params.quantity || 1;
        const auto_refill_enabled = params.auto_refill_enabled || false;
        // If we're adding to cart and we're in the c < i scenario, this means that there is no pricing option selected
        // and we should default to the cash option which is 'without_insurance'
        let selected_price_type: PriceType | undefined;
        if (cashIsLessThanInsurancePrescriptionIDs.includes(params.resource_id)) {
          selected_price_type = 'without_insurance';
        }

        let items: { resource_id: number; resource_type: string }[] = [];
        let cart_id = undefined;

        if (serverSideCartEnabled) {
          const result = await addToCartApi({
            ...params,
            quantity,
            auto_refill_enabled,
            selected_price_type,
          });

          if (!result.success) {
            return { success: false };
          }

          items = result.items;
          cart_id = result.cart_id;

          // If we've already entered checkout and hydrated the Redux cart with the server-side cart we need to add the new item to both places.
          if (isInCheckout) {
            addToCartRedux({
              ...params,
              auto_refill_enabled,
              selected_price_type,
              reset_order_date: params.resetOrderDate,
              billing_overrides: params.billing_overrides,
            });
          }
        } else {
          const result = addToCartRedux({
            ...params,
            auto_refill_enabled,
            selected_price_type,
            reset_order_date: params.resetOrderDate,
            billing_overrides: params.billing_overrides,
          });
          items = result.items;
        }

        if (serverSideCartEnabled) {
          queryClient.invalidateQueries({ queryKey: queries.cart.active({}).queryKey });
        }

        // trigger the cart middleware
        fireCartItemAddedEvent({
          resource_id: params.resource_id,
          auto_refill_enabled,
          selected_price_type,
          server_side_cart: serverSideCartEnabled,
          cart_items: items,
          cart_id,
        });

        const cartPrescriptionIDs = items
          .filter((item) => item.resource_type === 'Prescription')
          .map((item) => item.resource_id);
        await autobillAndRefreshData(params, cartPrescriptionIDs);

        setIsSuccess(true);
        return { success: true };
      } catch (e) {
        // invalidate homescreen for prescriptions that are no longer eligible to add to cart
        queryClient.invalidateQueries({ queryKey: homescreenMethods.fetchHomescreen({ userID }).queryKey });
        console.warn(e);
        addToast(<Toast variant="error">{genericErrorMessage}</Toast>);
        return { success: false };
      } finally {
        // finish any add-to-cart transactions if they're active
        SentrySpanHub.endSpan(SentrySpanHub.operations.addToCartFromHomescreen);
      }
    },
    [
      addToCartApi,
      addToCartRedux,
      addToast,
      autobillAndRefreshData,
      cashIsLessThanInsurancePrescriptionIDs,
      fireCartItemAddedEvent,
      queryClient,
      serverSideCartEnabled,
      userID,
      isInCheckout,
    ],
  );

  return {
    isSuccess,
    isPending,
    isDisabled: isPending || isFetching,
    handleAddToCart: addToCart,
  };
};
